import React, { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, DevLoginForm, Wrapper } from './components'
import { GlobalSettingsProvider } from './contexts'
import './index.css'
import { registerServiceWorker } from './utils/pushNotifications'

import { AccessColorMeta, AdminHubMeta, AdminSettingsMeta, AdminTrainingsMeta, AnnouncementMeta, CoreTeamMeta, DutyMeta, EventDetailMeta, EventsMeta, FlagMeta, HomeMeta, InternationalRegisterationsMeta, LoginMeta, MangementAnalyticsMeta, PreferenceMeta, TrainingModulePageMeta, UserMetaProps, VolunteerPoliciesMeta, VolunteerTrainingPageDetailsMeta, VolunteerTrainingPageMeta, AllocationStatusPageMeta, CheckInPageMeta, LiveOperationsMeta, PublicFormFillMeta, PublicFormsMeta, AdminGuidelinesMeta, VolunteerGuidelinesMeta, GuidelineDetailsMeta } from './data'

import { ProfileMeta as VolunteerProfileMeta, VolunteersMeta } from './data'
import { AccessColors, AdminHub, AdminSettings, AdminTrainings, AllocationStatus, Announcements, CoreTeamPage, Duties, EventDetail, Events, Flags, Home, LiveOperations, Login, Preferences, ProtectedRoute, PublicFormFill, PublicForms, TrainingModule, Unauthorized, Users, VolunteerPolicies, VolunteerTraining, VolunteerTrainingDetails, VolunteerGuidelines, GuidelineDetails, JVPoints, DirectorVolunteer } from './routes'
import { CheckIn } from './routes/CheckIn'

import EmailCampaigns from './routes/EmailCampaigns'
import EmailCampaignForm from './routes/EmailCampaignForm'
import { VolunteerProfile, Volunteers } from './routes'
import ForgetPassword from "./routes/forgetPassword"
import { HouseholdSelection } from './routes/HouseholdSelection'
import { InternationalRegisterations } from './routes/InternationalRegisterations'
import LoginForm from './routes/LoginForm'
import CheckInLoginForm from './routes/CheckInLoginForm'
import { ManagementAnalytics } from './routes/MangementAnalytics'
import ResetPassword from "./routes/resetPassword"
import Signup from "./routes/Signup"
import VerifyEmail from "./routes/VerifyEmail"
import { UserLogs } from './routes/UserLogs'
import { AdminGuidelines } from './routes/AdminGuidelines'
import { DirectorVolunteerMeta, JVPointsMeta } from './data/meta'

// Register service worker for push notifications
if ('serviceWorker' in navigator) {
    registerServiceWorker().catch(console.error);
}

// Set document direction and language based on stored preference
const storedLang = localStorage.getItem('language') || 'en'
document.documentElement.lang = storedLang
document.documentElement.dir = storedLang === 'fa' || storedLang === 'ar' ? 'rtl' : 'ltr'

createRoot(document.getElementById('root') as HTMLElement).render(
    <StrictMode>
        <AuthProvider>
            <GlobalSettingsProvider>
                <BrowserRouter>
                    <Routes>
                        {/* Public route */}
                        <Route path="/login" element={<Wrapper><Login meta={LoginMeta} /></Wrapper>} />
                        <Route path="/checkin-login" element={<Wrapper><CheckInLoginForm /></Wrapper>} />
                        <Route path="/household-selection" element={<HouseholdSelection />} />

                        {/* International visitor routes */}
                        <Route path="/international" element={<ProtectedRoute allowAnonymous={true} checkSettings={(settings) => settings.international_signups_enabled} />}>
                            <Route path="" element={<Navigate to="/" />} />
                            <Route path="login" element={<LoginForm />} />
                            <Route path="forgetpassword" element={<ForgetPassword />} />
                            <Route path="resetpassword" element={<ResetPassword />} />
                            <Route path="signup" element={<Signup />} />
                        </Route>

                        {/* Email verification route - public */}
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/forms/:slug" element={<PublicFormFill meta={PublicFormFillMeta} />} />

                        {/* Protected routes */}
                        <Route path="/" element={<ProtectedRoute requiredRole="any" />}>
                            <Route path="" element={<Home meta={HomeMeta} />} />
                            <Route path="profile" element={<VolunteerProfile meta={VolunteerProfileMeta} adminSide={false} />} />
                            <Route path="volunteer/training" element={<VolunteerTraining meta={VolunteerTrainingPageMeta} />} />
                            <Route path="volunteer/policies" element={<VolunteerPolicies meta={VolunteerPoliciesMeta} />} />
                            <Route path="volunteer/guidelines" element={<VolunteerGuidelines meta={VolunteerGuidelinesMeta} />} />
                            <Route path="volunteer/guidelines/:guidelineId" element={<GuidelineDetails meta={GuidelineDetailsMeta} />} />
                            <Route path="volunteer/training/:trainingId/details" element={<VolunteerTrainingDetails meta={VolunteerTrainingPageDetailsMeta} />} />
                            <Route path="volunteer/training/:trainingId/module/:moduleIndex" element={<TrainingModule meta={TrainingModulePageMeta} />} />
                        </Route>
                        <Route path="/" element={<ProtectedRoute requiredPermission='view_admin_hub' />}>
                            <Route path="admin" element={<AdminHub meta={AdminHubMeta} />} />
                            <Route path="admin/settings" element={<AdminSettings meta={AdminSettingsMeta} />} />
                            {/* <Route path="dashboard" element={<Dashboard meta={AdminDashboardMeta} />} />
                            <Route path="management" element={<ManagementInformation meta={MagenmentInformationMeta} />} /> */}
                            <Route path="management-analytics" element={<ProtectedRoute requiredPermission='view_stats' noWrapper />}>
                                <Route index element={<ManagementAnalytics meta={MangementAnalyticsMeta} />} />
                            </Route>
                            <Route path="live-stats" element={<ProtectedRoute requiredPermission='view_live_stats' noWrapper />}>
                                <Route index element={<LiveOperations meta={LiveOperationsMeta} />} />
                            </Route>
                            <Route path="user" element={<ProtectedRoute requiredPermission='manage_users' noWrapper />}>
                                <Route index element={<Users meta={UserMetaProps} />} />
                            </Route>
                            <Route path="events" element={<ProtectedRoute requiredPermission='view_events' noWrapper />}>
                                <Route index element={<Events meta={EventsMeta} />} />
                                <Route path=":eventId" element={<EventDetail meta={EventDetailMeta} />} />
                            </Route>
                            <Route path="duty" element={<ProtectedRoute requiredPermission='manage_duties' noWrapper />}>
                                <Route index element={<Duties meta={DutyMeta} />} />
                            </Route>
                            <Route path="preferences" element={<ProtectedRoute requiredPermission='manage_preferences' noWrapper />}>
                                <Route index element={<Preferences meta={PreferenceMeta} />} />
                            </Route>
                            <Route path="flags" element={<ProtectedRoute requiredPermission='manage_flags' noWrapper />}>
                                <Route index element={<Flags meta={FlagMeta} />} />
                            </Route>
                            <Route path="trainings" element={<ProtectedRoute requiredPermission='manage_trainings' noWrapper />}>
                                <Route index element={<AdminTrainings meta={AdminTrainingsMeta} />} />
                                <Route path=":trainingId" element={<VolunteerTrainingDetails meta={VolunteerTrainingPageDetailsMeta} adminMode={true} />} />
                                <Route path=":trainingId/module/:moduleIndex" element={<TrainingModule meta={TrainingModulePageMeta} adminMode={true} />} />
                            </Route>
                            <Route path="volunteer" element={<Volunteers meta={VolunteersMeta} />} />

                            <Route path="volunteer/:volunteerId/profile" element={<VolunteerProfile meta={VolunteerProfileMeta} adminSide={true} />} />
                            <Route path="announcements" element={<ProtectedRoute requiredPermission='manage_announcements' noWrapper />}>
                                <Route index element={<Announcements meta={AnnouncementMeta} />} />
                            </Route>
                            <Route path="admin/email-campaigns" element={<ProtectedRoute requiredAnyPermission={['manage_email_campaigns', 'approve_email_campaigns']} noWrapper />}>
                                <Route index element={<EmailCampaigns />} />
                                <Route path="create" element={<EmailCampaignForm />} />
                                <Route path=":campaignId/edit" element={<EmailCampaignForm />} />
                            </Route>
                            <Route path="admin/forms" element={<ProtectedRoute requiredPermission='manage_forms' noWrapper />}>
                                <Route index element={<PublicForms meta={PublicFormsMeta} />} />
                            </Route>
                            <Route path="admin/allocation-status" element={<ProtectedRoute requiredPermission='view_allocation_status' noWrapper />}>
                                <Route index element={<AllocationStatus meta={AllocationStatusPageMeta} />} />
                            </Route>
                            <Route path="access-color" element={<ProtectedRoute requiredPermission='manage_access_colors' noWrapper />}>
                                <Route index element={<AccessColors meta={AccessColorMeta} />} />
                            </Route>
                            <Route path="international-registerations" element={<ProtectedRoute noWrapper />}>
                                <Route index element={<InternationalRegisterations meta={InternationalRegisterationsMeta} />} />
                            </Route>
                            <Route path="admin/user-log" element={<ProtectedRoute requiredPermission='view_user_logs' noWrapper />}>
                                <Route index element={<UserLogs />} />
                            </Route>
                            {/* <Route path="guidelines" element={<ProtectedRoute requiredPermission='manage_guidelines' noWrapper />}>
                                <Route index element={<AdminGuidelines meta={AdminGuidelinesMeta} />} />
                            </Route> */}
                            <Route path="jv-points" element={<ProtectedRoute requiredPermission='manage_jv_points' noWrapper />}>
                                <Route index element={<JVPoints meta={JVPointsMeta} />} />
                            </Route>
                            <Route path="director/volunteer" element={<ProtectedRoute requiredPermission='view_director_volunteers' noWrapper />}>
                                <Route index element={<DirectorVolunteer meta={DirectorVolunteerMeta} />} />
                            </Route>
                        </Route>
                        <Route path="admin/check-in" element={<ProtectedRoute requiredPermission='manage_check_ins' />}>
                            <Route index element={<CheckIn />} />
                        </Route>
                        <Route path="/" element={<ProtectedRoute requiredPermission="view_core_team" />}>
                            <Route path="core-team" element={<CoreTeamPage meta={CoreTeamMeta} />} />
                        </Route>
                        {/* Catch-all */}
                        <Route path="*" element={<Wrapper><Unauthorized /></Wrapper>} />
                    </Routes>
                    {/* Dev Login Form - only shows in development */}
                    <DevLoginForm />
                </BrowserRouter>
            </GlobalSettingsProvider>
        </AuthProvider>
    </StrictMode>
)
